import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, Grid2X2, LayoutList, ChevronDown, ChevronUp } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import YouMayAlsoLike from '../components/YouMayAlsoLike';
import { useLanguage } from '../context/LanguageContext';
import { useBrandBackground } from '../context/BrandBackgroundContext';
import { pixelSearch, pixelViewCategory } from '../utils/metaPixel';
import './Products.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dubai/Arabic perfume brands - shown only on Dubai Perfumes page
const DUBAI_BRANDS = [
  'Lattafa',
  'Afnan',
  'Armaf',
  'Rasasi',
  'Swiss Arabian',
  'Al Haramain',
  'Ard Al Zaafaran',
  'Ajmal',
  'Maison Alhambra',
  'Fragrance World',
  'Paris Corner'
];

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedBrands, setSelectedBrands] = useState(() => {
    const brandsParam = searchParams.get('brands');
    return brandsParam ? brandsParam.split(',').filter(Boolean) : [];
  });
  const [selectedGender, setSelectedGender] = useState(searchParams.get('gender') || 'all');
  const [selectedScentProfiles, setSelectedScentProfiles] = useState(() => {
    const profilesParam = searchParams.get('scent_profiles');
    return profilesParam ? profilesParam.split(',').filter(Boolean) : [];
  });
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileGridCols, setMobileGridCols] = useState(1); // 1 or 2 columns on mobile
  const [expandedFilters, setExpandedFilters] = useState({
    gender: true,
    brand: true,
    scent: true,
  });
  const [collectionName, setCollectionName] = useState(null); // Name of current collection for header
  const [collectionInfo, setCollectionInfo] = useState(null); // { description, banner_image }

  // Meta Pixel: fire Search event (debounced) whenever the user searches
  useEffect(() => {
    const q = (searchQuery || '').trim();
    if (!q || q.length < 2) return;
    const timer = setTimeout(() => {
      pixelSearch(q);
    }, 900);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Meta Pixel: fire ViewCategory when the category filter changes to something specific
  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'all') {
      pixelViewCategory(selectedCategory);
    }
  }, [selectedCategory]);
  const { t, lang } = useLanguage();
  const { setSelectedBrands: setHeaderBrands } = useBrandBackground();

  // Update header background when brands change
  useEffect(() => {
    setHeaderBrands(selectedBrands);
    
    // Cleanup - reset on unmount
    return () => {
      setHeaderBrands([]);
    };
  }, [selectedBrands, setHeaderBrands]);

  // Fetch collection name when collection param changes
  useEffect(() => {
    const collectionSlug = searchParams.get('collection');
    
    // Skip for default all_products collection
    if (!collectionSlug || collectionSlug === 'all_products') {
      setCollectionName(null);
      setCollectionInfo(null);
      return;
    }
    
    const fetchCollectionName = async () => {
      try {
        const res = await fetch(`${API_URL}/api/collections`);
        if (res.ok) {
          const collections = await res.json();
          const collection = collections.find(c => c.slug === collectionSlug);
          if (collection) {
            // Use localized name based on language
            const name = lang === 'bg' 
              ? (collection.name_bg || collection.name) 
              : (collection.name_en || collection.name);
            setCollectionName(name);
            setCollectionInfo({
              description: lang === 'bg'
                ? (collection.description || collection.description_en)
                : (collection.description_en || collection.description),
              banner_image: collection.banner_image || null,
            });
          } else {
            setCollectionName(null);
            setCollectionInfo(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch collection name:', err);
        setCollectionName(null);
        setCollectionInfo(null);
      }
    };
    
    fetchCollectionName();
  }, [searchParams, lang]);

  // Lock body scroll while the mobile filters drawer is open
  useEffect(() => {
    if (filtersOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [filtersOpen]);

  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  // Scent profile options
  const SCENT_PROFILE_OPTIONS = [
    { key: 'sweet', label: t('scentSweet') || 'Sweet' },
    { key: 'fresh', label: t('scentFresh') || 'Fresh' },
    { key: 'citrus', label: t('scentCitrus') || 'Citrus' },
    { key: 'fruity', label: t('scentFruity') || 'Fruity' },
    { key: 'floral', label: t('scentFloral') || 'Floral' },
    { key: 'woody', label: t('scentWoody') || 'Woody' },
    { key: 'spicy', label: t('scentSpicy') || 'Spicy' },
    { key: 'aquatic', label: t('scentAquatic') || 'Aquatic' },
    { key: 'musky', label: t('scentMusky') || 'Musky' },
    { key: 'leather', label: t('scentLeather') || 'Leather' },
    { key: 'tobacco', label: t('scentTobacco') || 'Tobacco' },
    { key: 'oriental', label: t('scentOriental') || 'Oriental' },
    { key: 'vanilla', label: t('scentVanilla') || 'Vanilla' },
  ];

  // Fetch brands with gender filter
  const fetchBrands = async (gender) => {
    try {
      const params = new URLSearchParams();
      params.set('collection', 'all_products');
      if (gender && gender !== 'all') params.set('gender', gender);
      const brandRes = await fetch(`${API_URL}/api/products/brands?${params.toString()}`);
      if (brandRes.ok) {
        const brandData = await brandRes.json();
        // Filter out Dubai brands from the brand filter list
        const filteredBrands = brandData
          .map(b => b.name)
          .filter(name => !DUBAI_BRANDS.includes(name));
        setBrands(filteredBrands);
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const catRes = await fetch(`${API_URL}/api/products/categories`);
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData.map(c => ({ id: c.id, name: c.name })));
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchMeta();
  }, []);

  // Refetch brands when gender changes
  useEffect(() => {
    fetchBrands(selectedGender);
    // Only reset selected brands if NOT coming from URL
    // (URL brands are handled in the other useEffect)
  }, [selectedGender]);

  // URL is the single source of truth. This effect syncs local state
  // whenever the URL changes (initial mount, browser back/forward, in-app nav
  // via Link, or click handlers below). No urlSyncRef needed because there is
  // no state→URL effect fighting it.
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category') || 'all';
    const searchFromUrl = searchParams.get('search') || '';
    const genderFromUrl = searchParams.get('gender') || 'all';
    const brandsFromUrl = searchParams.get('brands');
    const profilesFromUrl = searchParams.get('scent_profiles');

    setSelectedCategory(categoryFromUrl);
    setSearchQuery(searchFromUrl);
    setSelectedGender(genderFromUrl);
    setSelectedBrands(brandsFromUrl ? brandsFromUrl.split(',').filter(Boolean) : []);
    setSelectedScentProfiles(profilesFromUrl ? profilesFromUrl.split(',').filter(Boolean) : []);
  }, [searchParams]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') params.set('category', selectedCategory);
        
        // Read brands from URL directly to avoid race condition
        const brandsFromUrl = searchParams.get('brands');
        const brandsToUse = brandsFromUrl 
          ? brandsFromUrl.split(',').filter(Boolean) 
          : selectedBrands;
        
        // Read scent profiles from URL directly to avoid race condition
        const profilesFromUrl = searchParams.get('scent_profiles');
        const profilesToUse = profilesFromUrl 
          ? profilesFromUrl.split(',').filter(Boolean) 
          : selectedScentProfiles;
        
        if (brandsToUse.length > 0) params.set('brands', brandsToUse.join(','));
        if (selectedGender !== 'all') params.set('gender', selectedGender);
        if (profilesToUse.length > 0) params.set('scent_profiles', profilesToUse.join(','));
        if (searchQuery) params.set('search', searchQuery);
        if (sortBy) params.set('sort', sortBy);
        
        // Use collection from URL if specified, otherwise default to "all_products"
        const collectionFromUrl = searchParams.get('collection');
        params.set('collection', collectionFromUrl || 'all_products');
        params.set('limit', '200');

        const res = await fetch(`${API_URL}/api/products?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setProducts(data.products);
          setTotalProducts(data.total);
        } else {
          setProducts([]);
          setTotalProducts(0);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setProducts([]);
        setTotalProducts(0);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
    // NOTE: state (selectedCategory etc.) is derived from searchParams via the
    // URL→state effect above. Depending on both would cause duplicate fetches.
    // We keep 'collection' as an explicit dep since it has no mirrored state var.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedBrands, selectedGender, selectedScentProfiles, sortBy, searchQuery, searchParams.get('collection')]);

  const categoryNames = {
    perfumes: t('perfumes'),
    makeup: t('makeup'),
    skincare: t('skincare'),
    haircare: t('haircare'),
    bodycare: t('bodyCare'),
    menscare: t('mensCare'),
  };

  const handleCategoryChange = (category) => {
    const params = new URLSearchParams(searchParams);
    if (category === 'all') params.delete('category'); else params.set('category', category);
    setSearchParams(params, { replace: true });
  };

  const handleGenderChange = (gender) => {
    const params = new URLSearchParams(searchParams);
    if (gender === 'all') params.delete('gender'); else params.set('gender', gender);
    setSearchParams(params, { replace: true });
  };

  const handleBrandToggle = (brand) => {
    const current = (searchParams.get('brands') || '').split(',').filter(Boolean);
    const next = current.includes(brand) ? current.filter((b) => b !== brand) : [...current, brand];
    const params = new URLSearchParams(searchParams);
    if (next.length > 0) params.set('brands', next.join(',')); else params.delete('brands');
    setSearchParams(params, { replace: true });
  };

  const handleScentProfileToggle = (profile) => {
    const current = (searchParams.get('scent_profiles') || '').split(',').filter(Boolean);
    const next = current.includes(profile) ? current.filter((p) => p !== profile) : [...current, profile];
    const params = new URLSearchParams(searchParams);
    if (next.length > 0) params.set('scent_profiles', next.join(',')); else params.delete('scent_profiles');
    setSearchParams(params, { replace: true });
  };

  // NOTE: state→URL sync effect intentionally removed.
  // URL is the single source of truth. All click handlers above write to URL
  // directly, and the URL→state effect (above) is the only sync direction.
  // This prevents the bidirectional race where the URL→state effect would
  // reset user selections back to the previous URL value on the same render.

  const clearFilters = () => {
    // URL is the single source of truth — preserve only the collection param
    const collectionParam = searchParams.get('collection');
    if (collectionParam) {
      setSearchParams({ collection: collectionParam });
    } else {
      setSearchParams({});
    }
  };

  const clearBrands = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('brands');
    setSearchParams(params, { replace: true });
  };

  const clearScentProfiles = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('scent_profiles');
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="products-page">
      <div className="container">
        {collectionInfo?.banner_image ? (
          <div className="collection-banner" data-testid="collection-banner" style={{ backgroundImage: `url(${collectionInfo.banner_image})` }}>
            <div className="collection-banner-overlay">
              <h1 className="hero-medium" data-testid="products-heading">{collectionName}</h1>
              {collectionInfo.description && (
                <p className="body-large collection-banner-desc" data-testid="collection-description">{collectionInfo.description}</p>
              )}
              <p className="body-regular collection-banner-count">
                {totalProducts === 1 
                  ? t('discoverCollectionSingular', { count: totalProducts })
                  : t('discoverCollection', { count: totalProducts })}
              </p>
            </div>
          </div>
        ) : (
          <div className="page-header section-padding-small">
            <h1 className="hero-medium" data-testid="products-heading">
              {collectionName || t('allProducts')}
            </h1>
            {collectionInfo?.description && (
              <p className="body-large mt-3" data-testid="collection-description" style={{ color: 'var(--text-secondary)' }}>
                {collectionInfo.description}
              </p>
            )}
            <p className="body-large mt-3" style={{ color: 'var(--text-secondary)' }}>
              {totalProducts === 1 
                ? t('discoverCollectionSingular', { count: totalProducts })
                : t('discoverCollection', { count: totalProducts })}
            </p>
          </div>
        )}

        <div className="products-layout">
          {filtersOpen && (
            <div
              className="filters-overlay"
              onClick={() => setFiltersOpen(false)}
              data-testid="filters-overlay"
            />
          )}
          <aside className={`filters-sidebar ${filtersOpen ? 'open' : ''}`} data-testid="filters-sidebar">
            <div className="filters-header">
              <h3 className="heading-3">{t('filters')}</h3>
              <button className="close-filters" onClick={() => setFiltersOpen(false)} data-testid="close-filters-btn">
                <X size={20} />
              </button>
            </div>

            <div className="filters-body">
            {/* Category filter hidden - only perfumes for now */}

            {/* Gender filter */}
            <div className="filter-group">
              <button className="filter-title-btn" onClick={() => toggleFilter('gender')}>
                <span>{t('gender') || 'Gender'}</span>
                {expandedFilters.gender ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedFilters.gender && (
                <div className="filter-options">
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="gender"
                      checked={selectedGender === 'all'}
                      onChange={() => handleGenderChange('all')}
                      data-testid="gender-radio-all"
                    />
                    <span>{t('all') || 'All'}</span>
                  </label>
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="gender"
                      checked={selectedGender === 'women'}
                      onChange={() => handleGenderChange('women')}
                      data-testid="gender-radio-women"
                    />
                    <span>{t('forWomen') || 'For Women'}</span>
                  </label>
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="gender"
                      checked={selectedGender === 'men'}
                      onChange={() => handleGenderChange('men')}
                      data-testid="gender-radio-men"
                    />
                    <span>{t('forMen') || 'For Men'}</span>
                  </label>
                </div>
              )}
            </div>

            <div className="filter-group">
              <button className="filter-title-btn" onClick={() => toggleFilter('brand')}>
                <span>{t('brand')}</span>
                {expandedFilters.brand ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedFilters.brand && (
                <>
                  <div className="filter-options scrollable">
                    {brands.map(brand => (
                      <label key={brand} className="filter-option checkbox">
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => handleBrandToggle(brand)}
                          data-testid={`brand-checkbox-${brand}`}
                        />
                        <span>{brand}</span>
                      </label>
                    ))}
                  </div>
                  {selectedBrands.length > 0 && (
                    <button 
                      className="clear-brands-btn"
                      onClick={clearBrands}
                    >
                      {t('clearBrands') || 'Clear brands'} ({selectedBrands.length})
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Scent Profile Filter */}
            <div className="filter-group">
              <button className="filter-title-btn" onClick={() => toggleFilter('scent')}>
                <span>{t('scentProfile') || 'Scent Profile'}</span>
                {expandedFilters.scent ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedFilters.scent && (
                <>
                  <div className="filter-options scrollable">
                {SCENT_PROFILE_OPTIONS.map(({ key, label }) => (
                  <label key={key} className="filter-option checkbox">
                    <input
                      type="checkbox"
                      checked={selectedScentProfiles.includes(key)}
                      onChange={() => handleScentProfileToggle(key)}
                      data-testid={`scent-profile-checkbox-${key}`}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {selectedScentProfiles.length > 0 && (
                <button 
                  className="clear-brands-btn"
                  onClick={clearScentProfiles}
                >
                  {t('clearScentProfiles') || 'Clear profiles'} ({selectedScentProfiles.length})
                </button>
              )}
              </>
              )}
            </div>

            <button className="btn-primary clear-filters-desktop" onClick={clearFilters} data-testid="clear-filters-btn">
              {t('clearAllFilters')}
            </button>
            </div>

            <div className="filters-footer-mobile">
              <button className="filters-footer-clear" onClick={clearFilters} data-testid="clear-filters-mobile-btn">
                {t('clearAllFilters')}
              </button>
              <button
                className="filters-footer-apply"
                onClick={() => setFiltersOpen(false)}
                data-testid="apply-filters-mobile-btn"
              >
                {t('showResults', { count: totalProducts })}
              </button>
            </div>
          </aside>

          <div className="products-main">
            <div className="products-controls">
              <button
                className="filters-toggle"
                onClick={() => setFiltersOpen(!filtersOpen)}
                data-testid="filters-toggle"
              >
                <SlidersHorizontal size={20} />
                <span>{t('filters')}</span>
              </button>

              {/* Mobile Grid Toggle - only visible on mobile */}
              <div className="mobile-grid-toggle" data-testid="mobile-grid-toggle">
                <button
                  className={`grid-btn ${mobileGridCols === 1 ? 'active' : ''}`}
                  onClick={() => setMobileGridCols(1)}
                  aria-label="1 column"
                >
                  <LayoutList size={18} />
                </button>
                <button
                  className={`grid-btn ${mobileGridCols === 2 ? 'active' : ''}`}
                  onClick={() => setMobileGridCols(2)}
                  aria-label="2 columns"
                >
                  <Grid2X2 size={18} />
                </button>
              </div>

              <div className="sort-controls">
                <label htmlFor="sort">{t('sortBy')}</label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                  data-testid="sort-select"
                >
                  <option value="popular">{t('sortPopular')}</option>
                  <option value="newest">{t('sortNewest')}</option>
                  <option value="name">{t('sortName')}</option>
                  <option value="price-low">{t('sortPriceLow')}</option>
                  <option value="price-high">{t('sortPriceHigh')}</option>
                </select>
              </div>
            </div>

            {products.length > 0 ? (
              <div className={`grid-product-showcase ${mobileGridCols === 2 ? 'mobile-two-cols' : ''}`} data-testid="products-grid">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="no-products" data-testid="no-products">
                <p className="body-large">
                  {loading ? t('loadingProducts') : t('noProductsFound')}
                </p>
                {!loading && (
                  <button className="btn-primary mt-4" onClick={clearFilters}>
                    {t('clearFilters')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {products.length > 0 && (
          <YouMayAlsoLike
            mode="cart"
            productIds={products.slice(0, 5).map((p) => p.id)}
            limit={6}
            minToShow={3}
            testId="products-you-may-also-like"
          />
        )}
      </div>
    </div>
  );
};

export default Products;
